import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertTriangle, Download, EyeOff, FileDown, Gauge, Link2, ListChecks, Trash2, Upload, Users2, Truck,
} from "lucide-react";

/**
 * Tachigrafo: i file .DDD delle carte autista e delle unità di bordo.
 *
 * Si trascinano dentro i file come escono dal lettore, tanti insieme, e
 * l'app capisce da sola cos'è ciascuno, lo lega al dipendente e al mezzo, e
 * ne ricava quello che serve alla documentazione del sistema integrato: km,
 * ore di guida, viaggi (dall'inserimento all'estrazione della carta), ore e
 * km per cantiere incrociando la pianificazione, per autista, per mezzo, per
 * mese; le giornate oltre i limiti del 561/2006 e le scadenze (scarico carta
 * ogni 28 giorni, unità di bordo ogni 90, taratura, carta).
 *
 * Il caricamento è della gestione; la consultazione anche dei tecnici.
 */
const giorno = (iso) => (iso ? String(iso).substring(0, 10).split("-").reverse().join("/") : "—");
const oraDi = (iso) => (iso ? String(iso).substring(11, 16) : "");
const ore = (min) => (min == null ? "—" : `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`);
const num = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT"));

const GRAVITA = {
  rosso: "bg-red-50 text-red-800 border-red-200",
  arancione: "bg-orange-50 text-orange-800 border-orange-200",
  giallo: "bg-amber-50 text-amber-800 border-amber-200",
  grigio: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATO_FILE = {
  nuovo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  caricato: "bg-emerald-50 text-emerald-700 border-emerald-200",
  gia_caricato: "bg-slate-100 text-slate-600 border-slate-200",
  errore: "bg-red-50 text-red-700 border-red-200",
};
const NOME_STATO = { nuovo: "nuovo", caricato: "caricato", gia_caricato: "già caricato", errore: "errore" };
const COPERTURA = {
  senza_carta: ["Nessuna carta", "bg-red-50 text-red-800 border-red-200"],
  carta_mai_scaricata: ["Carta mai scaricata", "bg-orange-50 text-orange-800 border-orange-200"],
  mai_scaricato: ["Unità di bordo mai scaricata", "bg-red-50 text-red-800 border-red-200"],
  scarico_in_ritardo: ["Scarico in ritardo", "bg-orange-50 text-orange-800 border-orange-200"],
  taratura_scaduta: ["Taratura scaduta", "bg-red-50 text-red-800 border-red-200"],
  in_regola: ["In regola", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};

const scaricaFile = (data, nome) => {
  const u = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = u; a.download = nome; a.click();
  window.URL.revokeObjectURL(u);
};

const iso = (d) => d.toISOString().substring(0, 10);
const oggi = () => new Date();
const PERIODI = [
  { nome: "Anno in corso", calcola: () => [`${oggi().getFullYear()}-01-01`, iso(oggi())] },
  { nome: "Ultimi 12 mesi", calcola: () => { const d = oggi(); d.setFullYear(d.getFullYear() - 1); return [iso(d), iso(oggi())]; } },
  { nome: "Ultimi 3 mesi", calcola: () => { const d = oggi(); d.setMonth(d.getMonth() - 3); return [iso(d), iso(oggi())]; } },
  { nome: "Anno scorso", calcola: () => [`${oggi().getFullYear() - 1}-01-01`, `${oggi().getFullYear() - 1}-12-31`] },
];

const SCHEDE = [
  ["autisti", "Autisti"], ["mezzi", "Mezzi"], ["mesi", "Mesi"], ["cantieri", "Cantieri"],
  ["viaggi", "Viaggi"], ["abbinamenti", "Abbinamenti"], ["file", "File caricati"],
];

export default function Tachigrafo() {
  const { user } = useAuth();
  const gestione = ["admin", "amministrativo"].includes(user?.role);
  const [pan, setPan] = useState(null);
  const [rie, setRie] = useState(null);
  const [carico, setCarico] = useState(true);
  const [periodo, setPeriodo] = useState(PERIODI[0].calcola());
  const [autista, setAutista] = useState("");
  const [mezzo, setMezzo] = useState("");
  const [scheda, setScheda] = useState("autisti");
  const [importa, setImporta] = useState(false);
  const [copertura, setCopertura] = useState(false);
  const [tutteSegn, setTutteSegn] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);

  const caricaPan = useCallback(async () => {
    try {
      const { data } = await api.get("/tachigrafo");
      setPan(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nel caricamento"); }
  }, []);

  const caricaRie = useCallback(async () => {
    try {
      const { data } = await api.get("/tachigrafo/riepilogo", {
        params: { dal: periodo[0], al: periodo[1], autista: autista || undefined, mezzo: mezzo || undefined },
      });
      setRie(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nel riepilogo"); }
    finally { setCarico(false); }
  }, [periodo, autista, mezzo]);

  useEffect(() => { caricaPan(); }, [caricaPan]);
  useEffect(() => { caricaRie(); }, [caricaRie]);

  const esporta = async () => {
    try {
      const { data, headers } = await api.get("/tachigrafo/esporta", {
        params: { dal: periodo[0], al: periodo[1], autista: autista || undefined, mezzo: mezzo || undefined },
        responseType: "blob",
      });
      const nome = /filename="?([^";]+)"?/.exec(headers["content-disposition"] || "")?.[1]
        || `Tachigrafo_${periodo[0]}_${periodo[1]}.xlsx`;
      scaricaFile(data, nome);
    } catch { toast.error("Errore nell'esportazione"); }
  };

  const scaricaOriginale = async (f) => {
    try {
      const { data } = await api.get(`/tachigrafo/file/${f.id}/scarica`, { responseType: "blob" });
      scaricaFile(data, f.nome);
    } catch { toast.error("File non disponibile"); }
  };

  const eliminaFile = async (f) => {
    if (!window.confirm(`Togliere «${f.nome}» con le giornate e i viaggi che ha scritto?`)) return;
    try {
      await api.delete(`/tachigrafo/file/${f.id}`);
      toast.success("File tolto");
      caricaPan(); caricaRie();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const segnalazioni = pan?.segnalazioni || [];
  const segnVisibili = tutteSegn ? segnalazioni : segnalazioni.slice(0, 6);
  const t = rie?.totali;

  if (carico && !pan) return <p className="text-sm text-slate-500">Caricamento...</p>;

  return (
    <div className="space-y-4" data-testid="pagina-tachigrafo">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-black text-slate-900 flex items-center gap-2">
            <Gauge className="w-6 h-6 text-[#0067b2]" /> Tachigrafo
          </h1>
          <p className="text-sm text-slate-500">
            Carte autista e unità di bordo (.DDD): km, ore di guida, viaggi, scadenze.
            {pan && <> {pan.conteggi.autisti} autisti · {pan.conteggi.veicoli} mezzi · {pan.conteggi.file} file.</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setCopertura(true)} data-testid="cosa-manca">
            <ListChecks className="w-4 h-4 mr-1" /> Cosa manca
          </Button>
          <Button variant="outline" onClick={esporta} data-testid="esporta-tachigrafo">
            <Download className="w-4 h-4 mr-1" /> Esporta Excel
          </Button>
          {gestione && (
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setImporta(true)}
                    data-testid="carica-ddd">
              <Upload className="w-4 h-4 mr-1" /> Carica file .DDD
            </Button>
          )}
        </div>
      </div>

      {segnalazioni.length > 0 && (
        <Card className="border-amber-200">
          <CardContent className="p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Segnalazioni ({segnalazioni.length})
            </div>
            {segnVisibili.map((s, i) => (
              <div key={i} className={`text-xs rounded border px-2 py-1 flex flex-wrap gap-x-2 ${GRAVITA[s.gravita] || GRAVITA.grigio}`}>
                <strong>{s.soggetto}</strong><span>{s.testo}</span>
                {s.scadenza && <span className="opacity-70">({giorno(s.scadenza)})</span>}
              </div>
            ))}
            {segnalazioni.length > 6 && (
              <button type="button" className="text-xs text-[#0067b2] underline"
                      onClick={() => setTutteSegn(!tutteSegn)}>
                {tutteSegn ? "Mostra meno" : `Mostra tutte (${segnalazioni.length})`}
              </button>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div className="flex gap-1 flex-wrap">
            {PERIODI.map((p) => (
              <Button key={p.nome} size="sm" variant="outline" onClick={() => setPeriodo(p.calcola())}>{p.nome}</Button>
            ))}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Dal</Label>
            <Input type="date" value={periodo[0]} className="h-8"
                   onChange={(e) => setPeriodo([e.target.value, periodo[1]])} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Al</Label>
            <Input type="date" value={periodo[1]} className="h-8"
                   onChange={(e) => setPeriodo([periodo[0], e.target.value])} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Autista</Label>
            <select className="h-8 border rounded px-2 text-sm bg-white" value={autista}
                    onChange={(e) => setAutista(e.target.value)} data-testid="filtro-autista">
              <option value="">Tutti</option>
              {(pan?.autisti || []).map((a) => (
                <option key={a.numero} value={a.numero}>{a.dipendente || a.nome_completo || a.numero}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Mezzo</Label>
            <select className="h-8 border rounded px-2 text-sm bg-white" value={mezzo}
                    onChange={(e) => setMezzo(e.target.value)} data-testid="filtro-mezzo">
              <option value="">Tutti</option>
              {(pan?.veicoli || []).map((v) => (
                <option key={v.chiave_targa} value={v.chiave_targa}>{v.identifier || v.targa}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            ["Km percorsi", num(t.km_mezzi), "dall'odometro dei mezzi"],
            ["Ore di guida", ore(t.guida_min || t.guida_min_mezzi), t.guida_min ? "dalle carte" : "dalle unità di bordo"],
            ["Viaggi", num(t.viaggi), "inserimento → estrazione carta"],
            ["Ore per viaggio", t.ore_per_viaggio == null ? "—" : t.ore_per_viaggio.toLocaleString("it-IT"), `${num(t.km_per_viaggio)} km medi`],
            ["Giornate di guida", num(t.giorni_guida), `${num(t.giorni_mezzo)} giornate mezzo`],
            ["Oltre i limiti", num(t.segnalazioni_561), "reg. 561/2006"],
          ].map(([n, v, s]) => (
            <Card key={n}><CardContent className="p-3">
              <div className="font-display text-2xl font-black tabular-nums text-slate-900">{v}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{n}</div>
              <div className="text-[11px] text-slate-400">{s}</div>
            </CardContent></Card>
          ))}
        </div>
      )}

      {rie?.esclusi && (rie.esclusi.prima_assunzione > 0 || rie.esclusi.mezzi_altrui > 0) && (
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <EyeOff className="w-3.5 h-3.5" /> Non contati: {rie.esclusi.prima_assunzione} giornate o viaggi prima
          dell&apos;assunzione, {rie.esclusi.mezzi_altrui} su mezzi che non sono di SGM.
        </p>
      )}

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {SCHEDE.map(([k, n]) => (
          <button key={k} type="button" onClick={() => setScheda(k)} data-testid={`scheda-${k}`}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${scheda === k ? "border-[#0067b2] text-[#0067b2] font-semibold" : "border-transparent text-slate-500"}`}>
            {n}
          </button>
        ))}
      </div>

      {rie && scheda === "autisti" && (
        <Tabella
          colonne={["Autista", "Giorni guida", "Km (carta)", "Ore guida", "Altro lavoro", "Viaggi", "Ore/viaggio", "Km viaggi", "Mezzi", "561"]}
          righe={rie.autisti.map((a) => [
            <button type="button" className="text-left text-[#0067b2] hover:underline" key="n"
                    onClick={() => setDettaglio({ tipo: "autista", chiave: a.carta })}>
              {a.autista}{!a.da_carta && <span className="text-[10px] text-slate-400 ml-1">(solo da unità di bordo)</span>}
            </button>,
            num(a.giorni_guida), num(a.km), ore(a.guida_min), ore(a.lavoro_min), num(a.viaggi),
            a.ore_per_viaggio == null ? "—" : a.ore_per_viaggio.toLocaleString("it-IT"), num(a.km_viaggi),
            a.mezzi.join(", "),
            a.segnalazioni_561 ? <span className="text-red-700 font-semibold">{a.segnalazioni_561}</span> : "0",
          ])}
          vuoto="Nessun autista nel periodo." />
      )}

      {rie && scheda === "mezzi" && (
        <Tabella
          colonne={["Mezzo", "Giorni in uso", "Km (odometro)", "Ore guida", "Viaggi", "Ore/viaggio", "Km viaggi", "Autisti", "Prossima taratura", "Senza carta"]}
          righe={rie.mezzi.map((m) => [
            <button type="button" className="text-left text-[#0067b2] hover:underline" key="n"
                    onClick={() => setDettaglio({ tipo: "veicolo", chiave: m.chiave_targa })}>
              {m.mezzo}{!m.da_veicolo && <span className="text-[10px] text-slate-400 ml-1">(solo dalle carte)</span>}
            </button>,
            num(m.giorni_in_uso), num(m.km), ore(m.guida_min + (m.secondo_min || 0)), num(m.viaggi),
            m.ore_per_viaggio == null ? "—" : m.ore_per_viaggio.toLocaleString("it-IT"), num(m.km_viaggi),
            m.autisti.join(", "), giorno(m.prossima_taratura),
            (m.eventi || {})["guida senza carta idonea"] || "0",
          ])}
          vuoto="Nessun mezzo nel periodo." />
      )}

      {rie && scheda === "mesi" && (
        <Tabella
          colonne={["Mese", "Km (mezzi)", "Km (carte)", "Ore guida (carte)", "Ore guida (mezzi)", "Giorni guida", "Viaggi", "Ore in viaggio", ""]}
          righe={rie.mesi.map((m) => {
            const max = Math.max(1, ...rie.mesi.map((x) => x.km_mezzi));
            return [m.nome, num(m.km_mezzi), num(m.km_autisti), ore(m.guida_min), ore(m.guida_min_mezzi),
              num(m.giorni_guida), num(m.viaggi), ore(m.minuti_viaggi),
              <div key="b" className="h-2 bg-[#0067b2]/70 rounded" style={{ width: `${Math.round(100 * m.km_mezzi / max)}px` }} />];
          })}
          vuoto="Niente nel periodo." />
      )}

      {rie && scheda === "cantieri" && (
        <>
          <p className="text-xs text-slate-500">
            Le ore e i km di ogni giornata vanno al cantiere dove la pianificazione metteva quella persona o quel mezzo.
            Quello che resta senza pianificazione sta nell&apos;ultima riga.
          </p>
          <Tabella
            colonne={["Cantiere", "Committente", "Giornate uomo", "Ore guida", "Km (carte)", "Giornate mezzo", "Km (mezzi)", "Viaggi", "Ore in viaggio", "Persone", "Mezzi"]}
            righe={rie.cantieri.map((c) => [
              c.cantiere, c.committente, num(c.giornate_uomo), ore(c.guida_min), num(c.km_autisti),
              num(c.giornate_mezzo), num(c.km_mezzi), num(c.viaggi), ore(c.minuti_viaggi),
              c.persone.join(", "), c.mezzi.join(", "),
            ])}
            vuoto="Niente nel periodo." />
        </>
      )}

      {rie && scheda === "viaggi" && (
        <>
          <p className="text-xs text-slate-500">
            Un viaggio va dall&apos;inserimento all&apos;estrazione della carta. {rie.viaggi_totali} viaggi nel periodo
            {rie.viaggi_totali > rie.viaggi.length && <>, qui gli ultimi {rie.viaggi.length}: gli altri sono nell&apos;Excel</>}.
          </p>
          <Tabella
            colonne={["Data", "Inserita", "Estratta", "Durata", "Km", "Mezzo", "Autista", "Origine"]}
            righe={rie.viaggi.map((v) => [
              giorno(v.inserita), oraDi(v.inserita), oraDi(v.estratta), ore(v.minuti), num(v.km), v.mezzo, v.autista,
              v.origine === "veicolo" ? "unità di bordo" : "carta",
            ])}
            vuoto="Nessun viaggio nel periodo." />
        </>
      )}

      {pan && scheda === "abbinamenti" && (
        <Abbinamenti pan={pan} gestione={gestione} onCambiato={() => { caricaPan(); caricaRie(); }} />
      )}

      {pan && scheda === "file" && (
        <Tabella
          colonne={["Caricato il", "File", "Tipo", "Di chi", "Periodo", "Giornate", "Viaggi", ""]}
          righe={pan.file.map((f) => [
            giorno(f.caricato_il), f.nome, f.tipo === "carta" ? "carta autista" : "unità di bordo", f.descrizione,
            `${giorno(f.periodo?.dal)} – ${giorno(f.periodo?.al)}`, num(f.giornate), num(f.usi),
            <div key="a" className="flex gap-1 justify-end">
              <Button size="sm" variant="ghost" onClick={() => scaricaOriginale(f)} title="Scarica il file originale">
                <FileDown className="w-4 h-4" />
              </Button>
              {gestione && (
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => eliminaFile(f)} title="Togli">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>,
          ])}
          vuoto="Nessun file caricato: si parte da «Carica file .DDD»." />
      )}

      {importa && (
        <ImportaFile onChiudi={() => setImporta(false)}
                     onFatto={() => { setImporta(false); caricaPan(); caricaRie(); }} />
      )}
      {dettaglio && (
        <Dettaglio {...dettaglio} periodo={periodo} onChiudi={() => setDettaglio(null)} />
      )}
      {copertura && <Copertura gestione={gestione} onChiudi={() => setCopertura(false)} />}
    </div>
  );
}

function Tabella({ colonne, righe, vuoto }) {
  if (!righe.length) return <p className="text-sm text-slate-500">{vuoto}</p>;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>{colonne.map((c, i) => <th key={i} className="text-left px-2 py-2 whitespace-nowrap">{c}</th>)}</tr>
        </thead>
        <tbody>
          {righe.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {r.map((v, j) => <td key={j} className="px-2 py-1.5 tabular-nums align-top">{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportaFile({ onChiudi, onFatto }) {
  const [files, setFiles] = useState([]);
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);

  const aggiungi = (lista) => {
    const nuovi = Array.from(lista || []);
    setFiles((f) => {
      const nomi = new Set(f.map((x) => x.name));
      return [...f, ...nuovi.filter((x) => !nomi.has(x.name))];
    });
    setEsito(null);
  };

  const manda = async (soloVerifica) => {
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("solo_verifica", soloVerifica ? "true" : "false");
    try {
      const { data } = await api.post("/tachigrafo/importa", fd,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`${data.conteggi.caricato || 0} file caricati`);
        onFatto();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally { setBusy(false); }
  };

  const nuovi = esito ? (esito.conteggi.nuovo || 0) : 0;

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Carica i file del tachigrafo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
            Carte autista (<code>C_…DDD</code>) e unità di bordo (<code>M_…DDD</code>) insieme, quanti si vuole:
            l&apos;app riconosce da sola cos&apos;è ciascuno. Un file già caricato si salta, e due scarichi che si
            sovrappongono non contano le giornate due volte. Prima si verifica, poi si carica.
          </p>
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center text-sm text-slate-500"
               onDragOver={(e) => e.preventDefault()}
               onDrop={(e) => { e.preventDefault(); aggiungi(e.dataTransfer.files); }}>
            Trascina qui i file, oppure
            <Input type="file" multiple accept=".ddd,.DDD,.tgd,.esm,.c1b,.v1b" className="mt-2"
                   onChange={(e) => aggiungi(e.target.files)} data-testid="file-ddd" />
          </div>
          {files.length > 0 && !esito && (
            <p className="text-xs text-slate-600">{files.length} file scelti: {files.map((f) => f.name).join(", ")}</p>
          )}
          {esito && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(esito.conteggi).map(([k, v]) => (
                  <span key={k} className={`px-2 py-0.5 rounded-full border ${STATO_FILE[k] || STATO_FILE.gia_caricato}`}>
                    {NOME_STATO[k] || k}: <strong>{v}</strong>
                  </span>
                ))}
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>{["File", "Tipo", "Di chi", "In elenco", "Periodo", "Giornate", "Viaggi", "Esito"].map((c) => (
                      <th key={c} className="text-left px-2 py-1">{c}</th>))}</tr>
                  </thead>
                  <tbody>
                    {esito.esiti.map((e, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1 break-all">{e.file}</td>
                        <td className="px-2 py-1">{e.tipo === "carta" ? "carta" : e.tipo === "veicolo" ? "unità di bordo" : "—"}</td>
                        <td className="px-2 py-1">{e.descrizione || "—"}</td>
                        <td className="px-2 py-1">
                          {e.dipendente || e.mezzo
                            ? <span className="text-emerald-700">{e.dipendente || e.mezzo}</span>
                            : e.tipo ? <span className="text-amber-700">non trovato: si abbina a mano</span> : "—"}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">{e.periodo ? `${giorno(e.periodo.dal)} – ${giorno(e.periodo.al)}` : "—"}</td>
                        <td className="px-2 py-1">{e.giornate ?? "—"}</td>
                        <td className="px-2 py-1">{e.usi ?? "—"}</td>
                        <td className="px-2 py-1">
                          <span className={`px-1.5 py-0.5 rounded border ${STATO_FILE[e.stato]}`}>{NOME_STATO[e.stato] || e.stato}</span>
                          {e.messaggio && <div className="text-red-700">{e.messaggio}</div>}
                          {e.stato === "gia_caricato" && <div className="text-slate-500">il {giorno(e.caricato_il)}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {!esito || esito.solo_verifica === false ? (
            <Button onClick={() => manda(true)} disabled={busy || !files.length} data-testid="verifica-ddd">
              {busy ? "Leggo..." : "Verifica"}
            </Button>
          ) : (
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => manda(false)}
                    disabled={busy || !nuovi} data-testid="carica-ddd-conferma">
              {busy ? "Carico..." : `Carica ${nuovi} file`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Abbinamenti({ pan, gestione, onCambiato }) {
  const [persone, setPersone] = useState([]);
  const [mezzi, setMezzi] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([api.get("/employees"), api.get("/equipment")]);
        setPersone(p.data || []);
        setMezzi(m.data || []);
      } catch { /* le tendine restano vuote */ }
    })();
  }, []);

  const abbinaAutista = async (numero, employee_id) => {
    try {
      await api.post(`/tachigrafo/autisti/${numero}/abbina`, { employee_id: employee_id || null });
      toast.success("Abbinamento salvato");
      onCambiato();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const abbinaVeicolo = async (chiave, equipment_id) => {
    try {
      await api.post(`/tachigrafo/veicoli/${chiave}/abbina`, { equipment_id: equipment_id || null });
      toast.success("Abbinamento salvato");
      onCambiato();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const etichetta = (modo) => ({
    esatto: "dal nome", parziale: "dal nome (parziale)", manuale: "a mano", omonimi: "omonimi: scegliere",
    ambiguo: "più somiglianti: scegliere", assente: "non trovato", vuoto: "senza nome",
  }[modo] || modo || "—");

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card><CardContent className="p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <Users2 className="w-3.5 h-3.5" /> Autisti (carte) → dipendenti
        </div>
        {pan.autisti.map((a) => (
          <div key={a.numero}
               className={`flex flex-wrap items-center gap-2 text-sm border-t border-slate-100 pt-2 ${
                 a.sostituita_da ? "opacity-60" : ""}`}>
            <div className="flex-1 min-w-[160px]">
              <div className="font-medium">{a.nome_completo || a.numero}</div>
              <div className="text-[11px] text-slate-500">
                carta {a.numero}{a.scade_il && <> · scade il {giorno(a.scade_il)}</>}
                {a.scaricata_da_carta
                  ? <> · scaricata il {giorno(a.ultimo_scarico)}</>
                  : <> · mai scaricata</>}
                {/* La carta rinnovata resta in archivio con le sue giornate,
                    ma non e' piu' lei a dire quando scade. */}
                {a.sostituita_da && (
                  <> · <span className="text-slate-400">sostituita dalla {a.sostituita_da}</span></>
                )}
              </div>
            </div>
            <div className="text-[11px] text-slate-500">{etichetta(a.abbinamento)}</div>
            {gestione ? (
              <select className="h-8 border rounded px-2 text-sm bg-white max-w-[220px]" value={a.employee_id || ""}
                      onChange={(e) => abbinaAutista(a.numero, e.target.value)}>
                <option value="">— nessuno —</option>
                {persone.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            ) : <span className={a.dipendente ? "text-emerald-700" : "text-amber-700"}>{a.dipendente || "non abbinato"}</span>}
          </div>
        ))}
        {!pan.autisti.length && <p className="text-sm text-slate-500">Nessun autista ancora.</p>}
      </CardContent></Card>

      <Card><CardContent className="p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <Truck className="w-3.5 h-3.5" /> Unità di bordo (targhe) → mezzi in elenco
        </div>
        {pan.veicoli.map((v) => (
          <div key={v.chiave_targa} className="flex flex-wrap items-center gap-2 text-sm border-t border-slate-100 pt-2">
            <div className="flex-1 min-w-[160px]">
              <div className="font-medium">{v.targa}</div>
              <div className="text-[11px] text-slate-500">
                {v.odometro != null && <>odometro {num(v.odometro)} km al {giorno(v.odometro_al)} · </>}
                taratura {giorno(v.prossima_taratura)} · scarico {giorno(v.ultimo_scarico)} → prossimo {giorno(v.prossimo_scarico)}
              </div>
            </div>
            <div className="text-[11px] text-slate-500">{etichetta(v.abbinamento)}</div>
            {gestione ? (
              <select className="h-8 border rounded px-2 text-sm bg-white max-w-[220px]" value={v.equipment_id || ""}
                      onChange={(e) => abbinaVeicolo(v.chiave_targa, e.target.value)}>
                <option value="">— nessuno —</option>
                {mezzi.map((m) => <option key={m.id} value={m.id}>{m.identifier}{m.brand ? ` · ${m.brand}` : ""}</option>)}
              </select>
            ) : <span className={v.identifier ? "text-emerald-700" : "text-amber-700"}>{v.identifier || "non abbinato"}</span>}
          </div>
        ))}
        {!pan.veicoli.length && <p className="text-sm text-slate-500">Nessuna unità di bordo ancora.</p>}
      </CardContent></Card>
      <p className="text-xs text-slate-500 lg:col-span-2 flex items-center gap-1">
        <Link2 className="w-3.5 h-3.5" /> L&apos;abbinamento fatto a mano resta anche ai caricamenti successivi.
      </p>
    </div>
  );
}

function Copertura({ gestione, onChiudi }) {
  const [d, setD] = useState(null);
  const [mostraEsclusi, setMostraEsclusi] = useState(false);
  const [cartaDi, setCartaDi] = useState(null);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get("/tachigrafo/copertura");
      setD(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const escludi = async (tipo, id, escludi) => {
    try {
      await api.post("/tachigrafo/esclusi", { tipo, id, escludi });
      carica();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const patenteC = async (employee_id, patente_c) => {
    try {
      await api.post("/tachigrafo/patente-c", { employee_id, patente_c });
      carica();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const Stato = ({ s }) => {
    const [n, cls] = COPERTURA[s] || [s, GRAVITA.grigio];
    return <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>{n}</span>;
  };
  const conteggio = (c) => Object.entries(c || {}).filter(([k]) => k !== "in_regola").reduce((s, [, v]) => s + v, 0);

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Cosa manca rispetto all&apos;elenco</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-slate-500">Caricamento...</p> : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span><strong>{conteggio(d.conteggi.dipendenti)}</strong> dipendenti da sistemare</span>
              <span><strong>{conteggio(d.conteggi.mezzi)}</strong> mezzi da sistemare</span>
              <label className="flex items-center gap-1 text-xs text-slate-600 ml-auto">
                <input type="checkbox" checked={mostraEsclusi} onChange={(e) => setMostraEsclusi(e.target.checked)} />
                mostra anche chi non ha la patente C e i mezzi messi da parte
              </label>
            </div>
            <p className="text-xs text-slate-500">
              Lo scarico si aspetta da chi ha la spunta «patente C» nella scheda del dipendente e dai mezzi in
              categoria {d.categorie_con_tachigrafo.join(", ").toLowerCase()}. Un autocarro senza tachigrafo si mette da
              parte con «senza tachigrafo» e non torna a chiedere.
            </p>
            <div className="grid lg:grid-cols-2 gap-4">
              <Card><CardContent className="p-3 space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <Users2 className="w-3.5 h-3.5" /> Dipendenti
                </div>
                {d.dipendenti.map((x) => (
                  <div key={x.id} className="flex flex-wrap items-center gap-2 text-sm border-t border-slate-100 pt-1">
                    <div className="flex-1 min-w-[150px]">
                      <div className="font-medium">{x.nome}</div>
                      <div className="text-[11px] text-slate-500">
                        {x.mansione}{x.assunto_il && <> · assunto il {giorno(x.assunto_il)}</>}
                        {x.ultimo_scarico && <> · scarico {giorno(x.ultimo_scarico)} → entro {giorno(x.prossimo_scarico)}</>}
                        {x.scade_il && <> · carta {x.carta} scade {giorno(x.scade_il)}</>}
                        {x.a_mano && <> · <span className="text-slate-400">scritta a mano</span></>}
                      </div>
                    </div>
                    <Stato s={x.stato} />
                    {gestione && (x.a_mano || !x.carta) && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                              data-testid={`carta-a-mano-${x.id}`}
                              title="Scrivi numero e scadenza della carta, in attesa del primo scarico"
                              onClick={() => setCartaDi(x)}>
                        {x.a_mano ? "correggi carta" : "scrivi la carta"}
                      </Button>
                    )}
                    {gestione && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" title="Toglie la spunta patente C"
                              onClick={() => patenteC(x.id, false)}>
                        niente patente C
                      </Button>
                    )}
                  </div>
                ))}
                {!d.dipendenti.length && (
                  <p className="text-sm text-slate-500">Nessun dipendente ha la spunta «patente C» nella scheda.</p>
                )}
                {mostraEsclusi && d.senza_patente.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 pt-2">Senza patente C</div>
                    {d.senza_patente.map((x) => (
                      <div key={x.id} className="flex flex-wrap items-center gap-2 text-sm border-t border-slate-100 pt-1 opacity-70">
                        <div className="flex-1 min-w-[150px]">
                          <div className="font-medium">{x.nome}</div>
                          <div className="text-[11px] text-slate-500">
                            {x.mansione}
                            {x.vista_dal_tachigrafo && <span className="text-amber-700"> · ma il tachigrafo lo ha visto guidare</span>}
                          </div>
                        </div>
                        {gestione && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => patenteC(x.id, true)}>
                            ha la patente C
                          </Button>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </CardContent></Card>
              <Card><CardContent className="p-3 space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <Truck className="w-3.5 h-3.5" /> Mezzi
                </div>
                {d.mezzi.filter((x) => mostraEsclusi || !x.escluso).map((x) => (
                  <div key={x.id} className={`flex flex-wrap items-center gap-2 text-sm border-t border-slate-100 pt-1 ${x.escluso ? "opacity-50" : ""}`}>
                    <div className="flex-1 min-w-[150px]">
                      <div className="font-medium">{x.mezzo} <span className="font-normal text-slate-500">{x.descrizione}</span></div>
                      <div className="text-[11px] text-slate-500">
                        {x.categoria.toLowerCase()}
                        {x.ultimo_scarico && <> · scarico {giorno(x.ultimo_scarico)} → entro {giorno(x.prossimo_scarico)}</>}
                        {x.prossima_taratura && <> · taratura {giorno(x.prossima_taratura)}</>}
                        {x.odometro != null && <> · {num(x.odometro)} km</>}
                      </div>
                    </div>
                    <Stato s={x.stato} />
                    {gestione && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                              onClick={() => escludi("mezzo", x.id, !x.escluso)}>
                        {x.escluso ? "rimetti" : "senza tachigrafo"}
                      </Button>
                    )}
                  </div>
                ))}
                {!d.mezzi.length && <p className="text-sm text-slate-500">Nessun autocarro in elenco.</p>}
              </CardContent></Card>
            </div>
          </div>
        )}
      </DialogContent>
      {cartaDi && (
        <CartaAMano persona={cartaDi} onChiudi={() => setCartaDi(null)}
                    onFatto={() => { setCartaDi(null); carica(); }} />
      )}
    </Dialog>
  );
}

/**
 * Numero e scadenza della carta scritti a mano, in attesa del primo scarico.
 *
 * Serve per l'autista appena assunto: la carta ce l'ha in tasca, il file
 * arrivera' fra settimane, e intanto la scadenza deve stare nello
 * scadenzario come tutte le altre. Appena il .DDD arriva, le date del file
 * sostituiscono queste: e' la carta a dire la verita', non quello che
 * abbiamo battuto.
 */
function CartaAMano({ persona, onChiudi, onFatto }) {
  const [numero, setNumero] = useState(persona.a_mano ? persona.carta || "" : "");
  const [scade, setScade] = useState(persona.a_mano ? persona.scade_il || "" : "");
  const [emessa, setEmessa] = useState("");
  const [busy, setBusy] = useState(false);

  const salva = async (togli) => {
    setBusy(true);
    try {
      const { data } = await api.post("/tachigrafo/autisti/a-mano", {
        employee_id: persona.id,
        numero: togli ? "" : numero.trim(),
        scade_il: togli ? null : scade,
        emessa_il: togli ? null : (emessa || null),
      });
      toast.success(togli ? "Carta tolta" : `Carta ${data.numero} segnata`, {
        description: togli ? undefined : "Da adesso la scadenza è nello scadenzario del personale.",
      });
      onFatto();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      {/* Finestra dentro un'altra finestra: Radix spegne i tocchi sul body
          finche' un dialogo e' aperto, e tutte le finestre stanno a z-50.
          Senza queste due righe la si vede ma non si puo' scrivere. */}
      <DialogContent className="max-w-md z-[60]" style={{ pointerEvents: "auto" }}>
        <DialogHeader><DialogTitle>Carta di {persona.nome}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
            Da usare finché non arriva il primo scarico: la scadenza entra subito nello scadenzario
            del personale. Quando carichi il file <code>.DDD</code> di questa carta, le date del file
            prendono il posto di queste.
          </p>
          <div>
            <Label>Numero della carta</Label>
            <Input value={numero} onChange={(e) => setNumero(e.target.value)}
                   placeholder="come è scritto sulla carta" data-testid="carta-numero" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scade il</Label>
              <Input type="date" value={scade} onChange={(e) => setScade(e.target.value)}
                     data-testid="carta-scade" />
            </div>
            <div>
              <Label>Rilasciata il</Label>
              <Input type="date" value={emessa} onChange={(e) => setEmessa(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-slate-500">La data di rilascio non è obbligatoria.</p>
        </div>
        <DialogFooter className="gap-2">
          {persona.a_mano && (
            <Button variant="outline" disabled={busy} onClick={() => salva(true)}
                    className="text-red-600 mr-auto">
              Togli
            </Button>
          )}
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button className="bg-orange-500 hover:bg-orange-600" data-testid="carta-salva"
                  disabled={busy || !numero.trim() || !scade} onClick={() => salva(false)}>
            {busy ? "Salvo..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Dettaglio({ tipo, chiave, periodo, onChiudi }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tachigrafo/${tipo === "autista" ? "autisti" : "veicoli"}/${chiave}`,
                                       { params: { dal: periodo[0], al: periodo[1] } });
        setD(data);
      } catch (err) { toast.error(err.response?.data?.detail || "Errore"); onChiudi(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, chiave, periodo]);

  const titolo = !d ? "..." : tipo === "autista"
    ? (d.autista.dipendente || d.autista.nome_completo)
    : (d.veicolo.identifier || d.veicolo.targa);
  const limiti = d?.limiti;
  const voci = limiti ? [
    ["Oltre 10 ore di guida", limiti.oltre_10h], ["Terza giornata estesa in settimana", limiti.terza_estesa_in_settimana],
    ["Oltre 4h30 di guida continua", limiti.oltre_4h30_continue], ["Oltre 56 ore in settimana", limiti.oltre_56h_settimana],
    ["Oltre 90 ore in due settimane", limiti.oltre_90h_due_settimane],
  ].filter(([, l]) => l && l.length) : [];

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{titolo}</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-slate-500">Caricamento...</p> : (
          <div className="space-y-3 text-sm">
            {tipo === "autista" ? (
              <p className="text-xs text-slate-500">
                Carta {d.autista.numero} · emessa il {giorno(d.autista.emessa_il)} · scade il {giorno(d.autista.scade_il)}
                {d.autista.ultimo_scarico && <> · ultimo scarico {giorno(d.autista.ultimo_scarico)} → prossimo entro {giorno(d.autista.prossimo_scarico)}</>}
                {d.autista.patente?.numero && <> · patente {d.autista.patente.numero}</>}
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                Targa {d.veicolo.targa} · telaio {d.veicolo.telaio || "—"} · tachigrafo {d.veicolo.tecnica?.costruttore || "—"}
                {d.veicolo.tecnica?.n_serie && <> n. {d.veicolo.tecnica.n_serie}</>}
                {" "}· taratura {giorno(d.veicolo.prossima_taratura)} · velocità massima {d.veicolo.velocita_massima ?? "—"} km/h
                {" "}· ultimo scarico {giorno(d.veicolo.ultimo_scarico)} → prossimo entro {giorno(d.veicolo.prossimo_scarico)}
              </p>
            )}
            {voci.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 space-y-1">
                {voci.map(([n, l]) => (
                  <div key={n}><strong>{n}:</strong> {l.map((x) => `${x.data ? giorno(x.data) : x.settimana || x.settimane} (${ore(x.minuti)})`).join(", ")}</div>
                ))}
              </div>
            )}
            {tipo === "veicolo" && Object.keys({ ...(d.veicolo.eventi || {}), ...(d.veicolo.guasti || {}) }).length > 0 && (
              <div className="flex flex-wrap gap-1 text-xs">
                {Object.entries(d.veicolo.eventi || {}).map(([k, v]) => (
                  <span key={k} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{k}: <strong>{v}</strong></span>
                ))}
                {Object.entries(d.veicolo.guasti || {}).map(([k, v]) => (
                  <span key={k} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">guasto {k}: <strong>{v}</strong></span>
                ))}
              </div>
            )}
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Giornate ({d.giornate.length})</div>
            <Tabella
              colonne={tipo === "autista"
                ? ["Data", "Guida", "Altro lavoro", "Disponibilità", "Riposo", "Guida continua max", "Km"]
                : ["Data", "Odometro", "Km", "Guida", "Altro lavoro", "Secondo conducente"]}
              righe={d.giornate.map((g) => tipo === "autista"
                ? [giorno(g.data), ore(g.minuti.guida), ore(g.minuti.lavoro), ore(g.minuti.disponibilita), ore(g.minuti.riposo),
                   g.guida_continua_max > 270 ? <span className="text-red-700">{ore(g.guida_continua_max)}</span> : ore(g.guida_continua_max), num(g.km)]
                : [giorno(g.data), num(g.odometro), num(g.km), ore(g.minuti.guida), ore(g.minuti.lavoro),
                   ore(g.secondo_conducente?.guida)])}
              vuoto="Nessuna giornata nel periodo." />
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Viaggi ({d.usi.length})</div>
            <Tabella
              colonne={["Data", "Inserita", "Estratta", "Durata", "Km", tipo === "autista" ? "Mezzo" : "Autista", "Origine"]}
              righe={d.usi.map((u) => [
                giorno(u.inserita), oraDi(u.inserita), oraDi(u.estratta), ore(u.minuti), num(u.km),
                tipo === "autista" ? u.targa : (u.autista || "—"),
                u.origine === "veicolo" ? "unità di bordo" : "carta",
              ])}
              vuoto="Nessun viaggio nel periodo." />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
